import {
  BenefitBackground,
  ListMarkerType,
  TextComponentPostion,
  TextComponentTypes,
  TextComponentTypesLeft,
} from '@allesauto/utils/consts';
import {
  createMarkup,
  FieldTypeAsset,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { getPcpUrlForContentPages } from '@allesauto/utils/routeUtils';
import { ArrowRight16 } from '@carbon/icons-react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useEffect, useState } from 'react';

import Button from '../button';
import CheckList from '../checklistContainer/checklist/CheckList';
import NextImage from '../nextImage';
import Testimonial, { TestimonialProps } from '../testimonial';
import TextLink from '../textLink';

const MIN_VALUE_LEASING_FILTER = 124;
const MAX_VALUE_LEASING_FILTER = 1113;

interface SectionAreaBase {
  title?: string;
  isTitleH2?: boolean;
  linkName?: string;
  linkUrl?: string;
  buttonName?: string;
  buttonURL?: string;
  filterLeasing?: boolean;
  component?: string;
}

interface SectionAreaWithBody extends SectionAreaBase {
  body: string;
  bodyHtml?: never;
}

interface SectionAreaWithHtml extends SectionAreaBase {
  body?: never;
  bodyHtml: string;
}

interface TestimonialComponent {
  testimonial: TestimonialProps;
  position: TextComponentPostion | string;
}

interface FullWidthParagraph {
  title: string;
  description?: string;
  items?: string[];
  itemsHtml?: string[];
  hasCheckMarkList?: boolean;
  descriptionAfterItems?: string;
  descriptionAfterItemsHtml?: string;
}

interface FullWidthComponent {
  title?: string;
  subTitle?: string;
  fullWidthParagraphs: FullWidthParagraph[];
  titleParagraphClassName?: string;
  itemsParagraphClassName?: string;
}

interface TextComponentLeftProps {
  isHeaderH1?: boolean;
  type: TextComponentTypesLeft | string;
  body: string | undefined;
  image: FieldTypeAsset;
}

interface TextComponentInterface {
  leftArea?: TextComponentLeftProps;
  sectionArea?: (SectionAreaWithBody | SectionAreaWithHtml)[];
  testimonialComponent?: TestimonialComponent;
  fullWidthComponent?: FullWidthComponent;
}

interface ITextComponents {
  textComponents: TextComponentInterface[];
  className?: string;
  type?: TextComponentTypes.ACCORDION;
  handleAction?: () => void;
}

/**
 * @deprecated
 *
 * This component adds support to the old structure from static JSON files. It can be deleted
 * as soon as all JSON files are replaced in favor of Storyblok
 */
const TextComponentLegacy: FC<ITextComponents> = (props: ITextComponents) => {
  const {
    className: customContainerClass = '',
    textComponents,
    type,
    handleAction,
  } = props;

  const [currentHref, setCurrentHref] = useState<string | URL>('');
  const router = useRouter();

  const gotoURL = (url: string) => {
    router.push(parseSlashToURL(url));
  };

  const getLeftSideComponent = (objectComponent: TextComponentInterface) => {
    const className = {
      headerTag: clsx(
        'break-words col-start-2 pb-40px sm:col-end-24 md:col-end-12',
        objectComponent.leftArea?.isHeaderH1 && 'text-headline2'
      ),
    };

    switch (objectComponent.leftArea && objectComponent.leftArea.type) {
      case TextComponentTypesLeft.TITLE: {
        if (objectComponent.leftArea?.isHeaderH1) {
          return (
            <h1 className={className.headerTag}>
              {objectComponent.leftArea &&
                (objectComponent.leftArea.body as ReactNode)}
            </h1>
          );
        } else {
          return (
            <h2 className={className.headerTag}>
              {objectComponent.leftArea &&
                (objectComponent.leftArea.body as ReactNode)}
            </h2>
          );
        }
      }
      case TextComponentTypesLeft.NONE: {
        return <div className="col-start-2 sm:col-end-24 md:col-end-12"></div>;
      }
      case TextComponentTypesLeft.TESTIMONIAL: {
        const className =
          objectComponent?.testimonialComponent?.position ===
          TextComponentPostion.LEFT
            ? 'col-start-2 sm:col-end-24 md:col-end-12 sm:pb-40px'
            : 'sm:col-start-2 md:col-start-14 col-end-24';
        return (
          <div className={className}>
            {objectComponent?.testimonialComponent?.testimonial && (
              <Testimonial
                {...objectComponent?.testimonialComponent?.testimonial}
              />
            )}
          </div>
        );
      }
      case TextComponentTypesLeft.PICTURE: {
        return (
          <div className="aspect-[16/10] col-start-2 relative sm:col-end-24 md:col-end-12">
            {objectComponent.leftArea?.image?.filename && (
              <NextImage
                useCustomLoader={false}
                alt={objectComponent.leftArea?.image?.alt}
                layout="fill"
                src={objectComponent.leftArea?.image?.filename}
                objectFit="cover"
              />
            )}
          </div>
        );
      }
    }
  };

  const accordionItems = (
    items: string[],
    hasCheckMarkList: boolean | undefined,
    itemsParagraphClassName?: string
  ) => {
    return !hasCheckMarkList ? (
      <div
        className={
          itemsParagraphClassName ? itemsParagraphClassName : 'pt-20px'
        }
      >
        {items.map((item, i) => (
          <div
            key={i}
            className="text-p4"
            dangerouslySetInnerHTML={createMarkup(item)}
          ></div>
        ))}
      </div>
    ) : (
      <CheckList
        list={{
          listItems: items,
          background: BenefitBackground.WHITE,
          markerType: ListMarkerType.BULLET,
          customClassName: {
            wrapperClassName: 'lg:p-0 lg:pb-20px',
          },
        }}
      />
    );
  };

  useEffect(() => {
    setCurrentHref(window.location.href);
  }, []);

  const constructHref = (
    urlLink: string,
    filterLeasing: boolean | undefined
  ): string => {
    if (currentHref && filterLeasing) {
      const url = getPcpUrlForContentPages(
        {
          key: 'LEASING_RATE_RANGE',
          min: MIN_VALUE_LEASING_FILTER,
          max: MAX_VALUE_LEASING_FILTER,
        },
        currentHref as string,
        urlLink
      );
      return url;
    }
    return parseSlashToURL(urlLink);
  };

  return (
    <div className={`bg-white sm:pt-40px md:pt-80px ${customContainerClass}`}>
      {textComponents &&
        textComponents.map((tComponent, index) => {
          return (
            <div
              key={index}
              className="grid grid-cols-24 sm:last:pb-0 sm:pb-40px md:pb-80px"
            >
              {!tComponent.fullWidthComponent &&
                getLeftSideComponent(tComponent)}
              <div
                className={
                  tComponent.fullWidthComponent
                    ? clsx(
                        type === TextComponentTypes.ACCORDION
                          ? 'sm:col-start-1 md:col-start-1'
                          : 'sm:col-start-2 md:col-start-4',
                        'col-end-24 relative'
                      )
                    : 'sm:col-start-2 md:col-start-14 relative sm:col-end-24 md:col-end-24'
                }
              >
                {(tComponent.testimonialComponent &&
                  tComponent.testimonialComponent.position ===
                    TextComponentPostion.RIGHT) ||
                  (tComponent.sectionArea &&
                    tComponent.sectionArea.map((sArea, i) => {
                      const sectionAreaClass = {
                        hTag: clsx(
                          'font-headline text-grey80 text-headline4',
                          tComponent.leftArea &&
                            tComponent.leftArea.type === 'Picture' &&
                            i === 0 &&
                            'mt-40px md:mt-0'
                        ),
                      };
                      return (
                        <div
                          key={i}
                          className="sm:pb-40px md:pb-80px last:pb-0"
                        >
                          {sArea.title && tComponent.leftArea?.isHeaderH1 && (
                            <h2 className={sectionAreaClass.hTag}>
                              {sArea.title}
                            </h2>
                          )}
                          {sArea.title && !tComponent.leftArea?.isHeaderH1 && (
                            <h3 className={sectionAreaClass.hTag}>
                              {sArea.title}
                            </h3>
                          )}
                          {'bodyHtml' in sArea && sArea.bodyHtml ? (
                            <div
                              className={clsx(
                                sArea.title && 'pt-40px',
                                sArea.linkName && 'pb-20px'
                              )}
                              dangerouslySetInnerHTML={createMarkup(
                                sArea.bodyHtml
                              )}
                            ></div>
                          ) : (
                            <p
                              className={clsx(
                                sArea.title && 'pt-40px',
                                sArea.linkName && 'pb-20px'
                              )}
                            >
                              {sArea.body}
                            </p>
                          )}
                          {sArea.linkName ? (
                            sArea.linkUrl !== 'overlay' ? (
                              <TextLink
                                text={sArea.linkName}
                                icon={<ArrowRight16 />}
                                href={
                                  sArea.linkUrl
                                    ? constructHref(
                                        sArea.linkUrl,
                                        sArea.filterLeasing
                                      )
                                    : '#'
                                }
                              />
                            ) : (
                              <TextLink
                                text={sArea.linkName}
                                icon={<ArrowRight16 />}
                                onClick={handleAction}
                              />
                            )
                          ) : (
                            ''
                          )}
                          {sArea.buttonName && (
                            <Button
                              customClassName="mt-20px"
                              text={sArea.buttonName}
                              onClick={() => gotoURL(sArea.buttonURL ?? '#')}
                            />
                          )}
                        </div>
                      );
                    }))}
                {tComponent.fullWidthComponent && (
                  <>
                    {tComponent.fullWidthComponent.title && (
                      <h3 className="font-headline text-grey100 text-headline3">
                        {tComponent.fullWidthComponent.title}
                      </h3>
                    )}
                    {tComponent.fullWidthComponent.subTitle && (
                      <p>{tComponent.fullWidthComponent.subTitle}</p>
                    )}
                    {tComponent.fullWidthComponent.fullWidthParagraphs.map(
                      (fwArea, i) => (
                        <div key={i}>
                          <p
                            className={clsx(
                              fwArea.title &&
                                clsx(
                                  (type === TextComponentTypes.ACCORDION &&
                                    i === 0) ||
                                    (tComponent.fullWidthComponent &&
                                      !tComponent.fullWidthComponent.title &&
                                      i === 0)
                                    ? 'pt-0px'
                                    : 'pt-30px',
                                  'font-weight-bold text-grey100 text-p4',
                                  tComponent.fullWidthComponent &&
                                    tComponent.fullWidthComponent
                                      .titleParagraphClassName
                                )
                            )}
                          >
                            {fwArea.title}
                          </p>
                          {fwArea.description && (
                            <p
                              className={clsx(
                                'pt-20px',
                                fwArea.hasCheckMarkList && 'pb-20px'
                              )}
                            >
                              {fwArea.description}
                            </p>
                          )}
                          {fwArea.itemsHtml &&
                            accordionItems(
                              fwArea.itemsHtml,
                              fwArea.hasCheckMarkList,
                              tComponent.fullWidthComponent
                                ?.itemsParagraphClassName
                            )}
                          {fwArea.items &&
                            accordionItems(
                              fwArea.items,
                              fwArea.hasCheckMarkList,
                              tComponent.fullWidthComponent
                                ?.itemsParagraphClassName
                            )}
                          {fwArea.descriptionAfterItems && (
                            <p>{fwArea.descriptionAfterItems}</p>
                          )}
                          {fwArea.descriptionAfterItemsHtml && (
                            <div
                              dangerouslySetInnerHTML={createMarkup(
                                fwArea.descriptionAfterItemsHtml
                              )}
                            ></div>
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default TextComponentLegacy;
